/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 29th 2024, 10:45:12 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ImageIcon from "@mui/icons-material/Image";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CropIcon from "@mui/icons-material/Crop";
import IconButton from "@mui/material/IconButton";
// import NotesIcon from "@mui/icons-material/Notes";
import Badge from "@mui/material/Badge";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Edit as EditIcon, Trash2 as TrashIcon } from "react-feather";
import { DateTimeWithDistance } from "common/components/DateTimeWithDistance";
import { useMediaLibraryImageAPI } from "media-library/services/MediaLibraryAPI";
import { useMediaLibraryStore, mediaLibraryStoreSelectors } from "media-library/store";
import { MediaLibrary, MediaLibraryImage } from "lib/schemas";
import { useCopyTextToClipBoard, formatBytes } from "lib/utils";
import {
  Image,
  OSActionButton,
  Center,
  StyledOSDialog,
  PopupMenu,
  APIListProgressWrapper,
  TextEditToggler,
  ImageCropper,
  AlertSnackBar,
} from "ui-widgets";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { AssetListSkeleton } from "./AssetListSkeleton";
import { EmptyLibraryCTA } from "./EmptyLibraryCTA";
import { LibraryAssetsTopBar } from "./LibraryAssetsTopBar";
import { LibraryAssetUploadForm } from "./LibraryAssetUploadForm";
import { LibraryItemContainer } from "./LibraryItemContainer";
import { TOnChooseMediaAsset } from "./types";

interface LibraryImagesProps {
  mediaLibraryId: MediaLibrary["id"];
  onChooseAsset?: TOnChooseMediaAsset;
}

export const LibraryImages = ({ mediaLibraryId, onChooseAsset }: LibraryImagesProps) => {
  const { useList } = useMediaLibraryImageAPI(mediaLibraryId);
  const { makeRequest: fetchImages, isMakingAPIRequest, isAPIError, httpResponseStatusCode } = useList();

  const { populateImages } = useMediaLibraryStore();
  const libraryImages = useMediaLibraryStore(mediaLibraryStoreSelectors.getLibrary(mediaLibraryId))?.mediaLibraryImages;

  React.useEffect(() => {
    if (libraryImages?.length) return;

    fetchImages({ setValidAPIResponse: (mediaLibraryImages) => populateImages(mediaLibraryId, mediaLibraryImages) });
  }, [fetchImages, populateImages, libraryImages?.length, mediaLibraryId]);

  return (
    <Stack spacing={4}>
      <LibraryAssetsTopBar
        title="Images"
        description="Upload images into your media library for easy reuse by you and your team."
        newUploadAction={
          <StyledOSDialog
            popUpTitle="Upload Images"
            popUpIcon={<ImageIcon color="primary" />}
            renderOpenButton={({ handleOpen }) => (
              <OSActionButton
                sx={{ marginLeft: "auto", alignSelf: "flex-start" }}
                variant="contained"
                actionStyle="custom"
                customIcon={<UploadIcon />}
                onClick={handleOpen}
              >
                Upload Images
              </OSActionButton>
            )}
            renderContentWithClose={({ handleClose }) => (
              <ImageUploadForm mediaLibraryId={mediaLibraryId} handleSaveDone={handleClose} />
            )}
          />
        }
      />
      <Grid container spacing={2}>
        <APIListProgressWrapper
          showLoader={isMakingAPIRequest}
          isAPIError={isAPIError}
          items={libraryImages}
          httpResponseStatusCode={httpResponseStatusCode}
          loaderType="custom"
          customProgressLoader={<AssetListSkeleton />}
          noDataMessage={<EmptyLibraryCTA mediaType="image" />}
        >
          {libraryImages?.map((imageRecord) => (
            <StyledOSDialog
              key={imageRecord.id}
              maxWidth="xl"
              popUpTitle={
                <Stack>
                  <Typography variant="h6" color="text.secondary">
                    {imageRecord.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {imageRecord.image_width} x {imageRecord.image_height} px
                  </Typography>
                </Stack>
              }
              popUpIcon={<ImageIcon color="primary" />}
              renderOpenButton={({ handleOpen }) => {
                const onClickImage = () => {
                  if (onChooseAsset)
                    onChooseAsset({
                      media_library_id: mediaLibraryId,
                      media_type: "image",
                      media_item_id: imageRecord.id,
                      media_item_url: imageRecord.image,
                      media_item_name: imageRecord.name,
                    });
                  else handleOpen();
                };
                return (
                  <Grid item xs={12} md={4} xl={3}>
                    <LibraryImageCard
                      imageRecord={imageRecord}
                      mediaLibraryId={mediaLibraryId}
                      onClick={onClickImage}
                    />
                  </Grid>
                );
              }}
            >
              <Center>
                <Image src={imageRecord.image} alt={imageRecord.name} />
              </Center>
            </StyledOSDialog>
          ))}
        </APIListProgressWrapper>
      </Grid>
    </Stack>
  );
};

interface LibraryImageCardProps {
  imageRecord: MediaLibraryImage;
  mediaLibraryId: MediaLibrary["id"];
  onClick?: () => void;
}

const LibraryImageCard = ({ imageRecord, mediaLibraryId, onClick }: LibraryImageCardProps) => {
  const { useDelete, useMutate } = useMediaLibraryImageAPI(mediaLibraryId);
  const { patchData } = useMutate();

  const { removeImage, addImage } = useMediaLibraryStore();
  const [editName, setEditName] = React.useState(false);

  const handleEditImageName = (imageRecordId: MediaLibraryImage["id"], newName: string) => {
    patchData({
      apiPathParams: [imageRecordId],
      body: JSON.stringify({ name: newName }),
      setValidAPIResponse: (mutatedImageRecord: MediaLibraryImage) => {
        addImage(mediaLibraryId, mutatedImageRecord);
      },
    });
  };

  const { copied, copyTextToClipBoard } = useCopyTextToClipBoard();
  const [showCopiedAlert, setShowCopiedAlert] = React.useState(false);
  return (
    <>
      <AlertSnackBar
        open={copied && showCopiedAlert}
        message={`URL for ${imageRecord.name} copied to clipboard.`}
        severity="success"
        vertical={"top"}
        horizontal={"center"}
        handleClose={() => setShowCopiedAlert(false)}
        canBeClosedByUser={false}
        autoClose
      />
      <LibraryItemContainer onClick={onClick}>
        <Image
          src={imageRecord.thumbnail || imageRecord.image}
          alt={imageRecord.name}
          style={{ height: "100%", objectFit: "scale-down" }}
        />
      </LibraryItemContainer>
      <Stack direction="row">
        <TextEditToggler
          handleEdit={(newName) => (newName === imageRecord.name ? null : handleEditImageName(imageRecord.id, newName))}
          value={imageRecord.name}
          forceEditMode={editName}
          setForceEditMode={setEditName}
        />

        <div>
          <DeleteOSItemDialog<MediaLibraryImage>
            itemId={imageRecord.id}
            useDeleteOSItem={useDelete}
            onDelete={() => removeImage(mediaLibraryId, imageRecord.id)}
            onRestore={(restoredImageRecord) => addImage(mediaLibraryId, restoredImageRecord)}
          >
            {({ handleOpen: handleOpenDeleteImageRecord }) => (
              <PopupMenu
                menuItems={[
                  {
                    icon: <EditIcon size="16" />,
                    onClick: () => setEditName(true),
                    text: "Rename Image",
                  },
                  // { icon: <NotesIcon />, onClick: null, text: "Add caption" },
                  {
                    icon: <ContentCopyIcon />,
                    onClick: () => {
                      copyTextToClipBoard(imageRecord.image);
                      setShowCopiedAlert(true);
                    },
                    text: "Copy Link",
                  },
                  {
                    icon: <TrashIcon size="16" />,
                    onClick: handleOpenDeleteImageRecord,
                    text: "Delete Image",
                  },
                ]}
              />
            )}
          </DeleteOSItemDialog>
        </div>
      </Stack>
      <Typography variant="body1" color="text.secondary">
        <DateTimeWithDistance prefix={<>Added</>} dateTime={imageRecord.created_at} />
      </Typography>
    </>
  );
};

const validImageTypes = ["image/gif", "image/jpeg", "image/png"];

const isValidImage = (file: File) => validImageTypes.includes(file.type);

const ImageUploadForm = ({ mediaLibraryId, handleSaveDone }: { handleSaveDone?: () => void } & LibraryImagesProps) => {
  const { useAPIForm } = useMediaLibraryImageAPI(mediaLibraryId);
  const { addImage } = useMediaLibraryStore();
  const useAPIFormReturn = useAPIForm({
    id: null,
    onAPISave: (imageRecordFromAPI) => {
      addImage(mediaLibraryId, imageRecordFromAPI);
      handleSaveDone?.();
    },
    defaultValues: {
      image: null,
      library: mediaLibraryId,
    },
  });

  const onAddFiles = (files: File[]) => {
    useAPIFormReturn.useFormReturn.setValue("image", (files?.length > 0 ? files : null) as unknown as string);
  };
  const watchedImages = useAPIFormReturn.useFormReturn.watch("image");
  const uploadedFiles = watchedImages ? (Array.from(watchedImages) as unknown as File[]) : null;
  const isValid = uploadedFiles?.every(isValidImage);

  return (
    <LibraryAssetUploadForm<MediaLibraryImage>
      useAPIFormReturn={useAPIFormReturn}
      uploadedFiles={uploadedFiles}
      onAddFiles={onAddFiles}
      dropTargetText="Drag image files from your local machine and drop them here to upload"
      multiple
      filePreviewProps={{
        validFileMessage: null,
        validFileIcon: (
          <Grid container spacing={2} sx={{ padding: "50px", minWidth: "500px" }}>
            {uploadedFiles?.map((uploadedFile, index) => (
              <Grid key={`${index}`} item xs={12} md={4} xl={3}>
                <ImageAssetUploadPreview
                  key={`${index}`}
                  uploadedFile={uploadedFile}
                  onEditFile={(modifiedFile) => {
                    const modifiedFiles = uploadedFiles.slice();
                    modifiedFiles[index] = modifiedFile;
                    onAddFiles(modifiedFiles);
                  }}
                  onDeleteFile={() => {
                    const modifiedFiles = uploadedFiles.slice();
                    modifiedFiles.splice(index, 1);
                    onAddFiles(modifiedFiles);
                  }}
                />
                <p>{uploadedFile?.name}</p>
                <Typography variant="subtitle1">
                  {formatBytes(uploadedFile?.size)} | {uploadedFile?.type}
                </Typography>
              </Grid>
            ))}
          </Grid>
        ),
        invalidFileMessage: null,
        invalidFileIcon: (
          <Grid container spacing={2} sx={{ padding: "50px", minWidth: "500px" }}>
            {uploadedFiles?.map((uploadedFile, index) => (
              <Grid key={`${index}`} item xs={12} md={4} xl={3}>
                {isValidImage(uploadedFile) ? (
                  <Image src={URL.createObjectURL(uploadedFile)} style={{ maxWidth: "100px" }} />
                ) : (
                  <p>{uploadedFile?.name} is not a valid image.</p>
                )}
              </Grid>
            ))}
          </Grid>
        ),
      }}
      isValid={isValid}
      showSubmitButton={isValid}
      chooseButtonLabel="Upload Images From Local Machine"
    />
  );
};

interface ImageAssetUploadPreviewProps {
  uploadedFile: File;
  onEditFile: (file: File) => void;
  onDeleteFile?: () => void;
}

const ImageAssetUploadPreview = ({ uploadedFile, onEditFile, onDeleteFile }: ImageAssetUploadPreviewProps) => {
  const uploadTempURL = URL.createObjectURL(uploadedFile);
  return uploadTempURL ? (
    <Badge
      badgeContent={
        <StyledOSDialog
          popUpTitle={`Crop Image: ${uploadedFile.name}`}
          popUpIcon={<CropIcon color="primary" />}
          renderOpenButton={({ handleOpen }) => (
            <>
              <Tooltip title={<Typography>Crop Image</Typography>}>
                <IconButton onClick={handleOpen}>
                  <CropIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title={<Typography>Remove</Typography>}>
                <IconButton onClick={onDeleteFile}>
                  <CloseIcon color="error" />
                </IconButton>
              </Tooltip>
            </>
          )}
          renderContentWithClose={({ handleClose }) => (
            <ImageCropper
              imageToCropName={uploadedFile.name}
              imageToCropURL={uploadTempURL}
              onCropImage={(croppedImage) => {
                onEditFile(croppedImage);
                handleClose();
              }}
              onCancelCrop={() => {
                handleClose();
              }}
            >
              {(cropState) => <Image src={uploadTempURL} style={{ maxWidth: "150px" }} />}
            </ImageCropper>
          )}
        />
      }
    >
      <Image src={uploadTempURL} style={{ maxWidth: "100px" }} />
    </Badge>
  ) : null;
};
